import AddArticleComment from 'components/Common/AddComment/AddArticleComment';
import { CoverPicture } from 'components/Profile/CoverPicture';
import { thumbUrl } from 'helpers/common';
import renderIcon from 'helpers/renderIcon';
import { IArticleComment } from 'models/magazine/article/articlecomment';
import { IUserPreferences } from 'models/user/userpreferences';
import moment = require('moment');
import * as React from 'react';
import { IEditArticleComment } from 'models/magazine/article/editarticlecomment';

import { IStore } from 'redux/IStore';
import EditArticleComment from './EditArticleComment';
import { deleteArticleComment } from 'redux/modules/magazine/article/articlecomments';
import { IArticleCommentsAction } from 'models/magazine/article/articlecomments';
import { LikeNodeType } from 'models/enums/nodetype/likenodetype';
import { Likes } from 'redux/modules/user/likes';
import { IAuthContext } from 'models/common';
import { withAuthContext } from 'containers/AccessControl/WithAuthContext';

const { connect } = require('react-redux');

interface IProps {
	comment: IArticleComment;
	functions: {
		t?: (key: string) => string;
	};
	userPreferences: IUserPreferences;
	replies?: IArticleComment[];
	articleId: string;
	isReply?: boolean;
	handleSubmit?: any;
	editArticleComment?: IEditArticleComment;
	deleteArticleComment?: (commentId: string) => IArticleCommentsAction;
	likeArticleComment?: (commentId: string) => any;
	unlikeArticleComment?: (commentId: string) => any;
	isCommentLiked?: (commentId: string, userId: string) => any;
	context?: IAuthContext;
}

export const useOutsideAlerter = (ref, setField, value) => {
	React.useEffect(() => {
		// hide elements when clicking outside of them
		const handleClickOutside = (event) => {
			if (ref.current && !ref.current.contains(event.target)) {
				setField(value);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			// Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ref]);
};

const useFocus = () => {
	const htmlElRef = React.useRef<HTMLInputElement>(null);
	const setFocus = () => {
		htmlElRef.current && htmlElRef.current.focus();
	};
	return { htmlElRef, setFocus };
};

let ArticleComment = (props: IProps) => {
	const [seeMore, setSeeMore] = React.useState<boolean>(false);
	const [showReplies, setShowReplies] = React.useState<boolean>(false);
	const [showReplyInput, setReplyInput] = React.useState<boolean>(false);
	const [options, setOptions] = React.useState<boolean>(false);
	const [expandableParagraph, setExpandableParagraph] = React.useState<boolean>(false);
	const { htmlElRef, setFocus } = useFocus();
	const [autoFocus, setAutoFocus] = React.useState<boolean>(false);
	const [toggleEdit, setToggleEdit] = React.useState<boolean>(false);
	const [commentBody, setCommentBody] = React.useState<string>(props?.comment?.body);
	const { isAuthenticated, signinRedirect } = props.context;

	const replyRef = React.useRef(null);
	const optionsMenuRef = React.useRef(null);
	const commentRef = React.useRef<HTMLParagraphElement>(null);

	useOutsideAlerter(replyRef, setReplyInput, false); // hide reply input when clicking outside
	useOutsideAlerter(optionsMenuRef, setOptions, false); // hide options menu when clicking outside

	const { addedDate, addedBy, addedByPicture } = props.comment;
	const { t } = props.functions;

	const isCommentExpandable = () => {
		if (commentRef && !expandableParagraph) {
			if (commentRef.current.scrollHeight - commentRef.current.offsetHeight > 5) {
				setExpandableParagraph(true);
			}
		} else if (commentRef && expandableParagraph) {
			// if we edited comment and it is not expandable anymore, we need to set expandableParagraph to false
			if (commentRef.current.scrollHeight - commentRef.current.offsetHeight <= 5) {
				setExpandableParagraph(false);
			}
		}
	};

	const checkLiked = () => {
		props.isCommentLiked(props.comment.id, props.userPreferences?.id);
	};

	React.useEffect(() => {
		// check if comment is liked by the current user
		checkLiked();
		// check if comment is expandable on component mount/ update
		isCommentExpandable();
	}, []);

	React.useEffect(() => {
		// check if comment is expandable on comment body update
		isCommentExpandable();
	}, [commentBody]);

	React.useEffect(() => {
		// Auto focus reply input when clicking reply
		if (showReplyInput && autoFocus) {
			// autoFocus for auto focusing only input when clicking reply icon not when clicking show replies
			setFocus();
		}
	}, [showReplyInput]);

	React.useEffect(() => {
		if (
			props.editArticleComment?.articleCommentUpdated &&
			props.comment.id === props.editArticleComment?.articleComment?.id
		) {
			setCommentBody(props.editArticleComment?.articleComment?.body);
		} else if (props.comment.data && props.comment.body != commentBody) {
			setCommentBody(props.comment.body);
		}
	}, [props.editArticleComment]);

	const toggleSeeMore = () => {
		setSeeMore(!seeMore);
	};

	const toggleShowReplies = () => {
		setAutoFocus(false);
		setShowReplies(!showReplies);
		setReplyInput(!showReplies);
	};

	const toggleReply = () => {
		setAutoFocus(true);
		setReplyInput(true);
		setShowReplies(true);
	};

	const toggleOptionsMenu = () => {
		setOptions(!options);
	};

	const likeComment = () => {
		props.likeArticleComment(props.comment.id);
	};

	const unlikeComment = () => {
		props.unlikeArticleComment(props.comment.id);
	};

	const handleLike = () => {
		if (!isAuthenticated()) {
			signinRedirect();
			return;
		}
		if (props.comment.isLiked) {
			unlikeComment();
		} else {
			likeComment();
		}
	};

	const deleteComment = async () => {
		if (confirm(props.functions.t('Magazine_Article_ArticleComments_DeleteUserMessage'))) {
			// if comment has replies, they are automatically deleted with parent comment on BE
			props.deleteArticleComment(props.comment.id);

			toggleOptionsMenu();
		}
	};

	const toggleEditComment = () => {
		setToggleEdit(!toggleEdit);
	};

	const optionsAvailable = {
		edit: props.userPreferences?.email === props.comment?.addedByEmail, // should compare with user id, but api doesn't return comment property addedById (props.userPreferences.id === props.comment.addedById)
		delete: props.userPreferences?.email === props.comment?.addedByEmail || props.userPreferences?.isAdministrator,
		report: false,
	};

	const optionsMenu = () => {
		// TODO:
		//       - add translations for the options menu

		return (
			<div className="options" ref={optionsMenuRef}>
				{optionsAvailable.edit && (
					<div
						className="option"
						onClick={() => {
							toggleEditComment();
							toggleOptionsMenu();
						}}
					>
						{renderIcon('edit', 22, 22, 'pointer')}
						<div>{t('BSPortal_Common_Edit')}</div>
					</div>
				)}
				{optionsAvailable.delete && (
					<div className="option" onClick={() => deleteComment()}>
						{renderIcon('delete_2', 18, 18, 'pointer')}
						<div>{t('BSPortal_Common_Delete')}</div>
					</div>
				)}
				{optionsAvailable.report && (
					<div
						className="option"
						// TODO: add report functionality
					>
						{renderIcon('notify', 18, 18, 'pointer')}
						<div>Report {/* {TODO: add to the resource files}*/}</div>
					</div>
				)}
			</div>
		);
	};

	const renderReplies = () =>
		props.replies
			.map((reply) => (
				<ArticleComment
					articleId={props.articleId}
					functions={props.functions}
					comment={reply}
					userPreferences={props.userPreferences}
					key={reply.id}
					isReply={true}
				/>
			))
			.reverse(); // reverse to show newest replies at the bottom of a reply section

	return (
		<div className={`articleComment ${props.isReply ? 'reply' : ''}`} ref={replyRef}>
			<CoverPicture imageUrl={addedByPicture ? thumbUrl(addedByPicture, 'small') : ''} editFunction={null} />
			<div className="textContainer">
				<div className="topCommentRow">
					<p className="p14 name">
						<span>{addedBy}</span>
						<span className="time">{addedDate && moment(addedDate).fromNow()}</span>
					</p>

					{optionsAvailable.delete || optionsAvailable.edit ? (
						<div className="moreIcon">{renderIcon('more', 22, 22, 'pointer', toggleOptionsMenu)}</div>
					) : (
						''
					)}
					{options && optionsMenu()}
				</div>
				<div className="body">
					{toggleEdit ? ( // when editing show input field with current comment body
						<EditArticleComment
							comment={{ ...props.comment, body: commentBody }}
							t={t}
							toggleEdit={toggleEditComment}
						/>
					) : (
						<>
							<p
								className={`${seeMore ? 'expanded' : ''}`}
								ref={commentRef}
								dangerouslySetInnerHTML={{ __html: commentBody }}
							/>
							{expandableParagraph ? (
								<div className="buttons">
									{seeMore ? (
										<p className="pointer" onClick={() => toggleSeeMore()}>
											{t('User_UserCard_SeeLess')}
										</p>
									) : (
										<p className="pointer" onClick={() => toggleSeeMore()}>
											{t('User_UserCard_SeeMore')}
										</p>
									)}
								</div>
							) : null}
						</>
					)}
				</div>
				<div className="articleInteractions">
					<div className="interact">
						<div className="likeIcon">
							{props.comment?.isLiked
								? renderIcon('liked', 22, 22, 'pointer', handleLike)
								: renderIcon('like', 22, 22, 'pointer', handleLike)}
						</div>
						<div>{props.comment.likesCount}</div>
					</div>
					{props.isReply ? ( // if comment is a reply we don't want to show reply icon
						''
					) : (
						<div className="interact">
							<div className="replyIcon" onClick={() => (isAuthenticated() ? setFocus() : signinRedirect())}>
								{renderIcon('comments', 22, 22, 'pointer', toggleReply)}
							</div>
							<div>{props.replies ? props.replies.length : 0}</div>
						</div>
					)}
				</div>
				<div className="replies">
					{props.replies && props.replies.length > 0 ? (
						showReplies ? (
							<>
								<p className="pointer showReplies" onClick={() => toggleShowReplies()}>
									{t('User_UserCard_HideReplies')}
								</p>
								{renderReplies()}
							</>
						) : (
							<p className="pointer showReplies" onClick={() => toggleShowReplies()}>
								{t('User_UserCard_ShowReplies')}
							</p>
						)
					) : null}
					<div className={showReplyInput ? 'addReply' : ''}>
						{showReplyInput ? (
							<AddArticleComment
								articleId={props.articleId}
								t={t}
								parentComment={props.comment.id}
								key={props.comment.id}
								inputRef={htmlElRef}
							/>
						) : null}
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToPropsArticleComment = (state: IStore) => {
	return {
		editArticleComment: state.editArticleCommentReducer,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		deleteArticleComment: (commentId: string) => dispatch(deleteArticleComment(commentId)),
		likeArticleComment: (commentId: string) =>
			dispatch(new Likes(LikeNodeType.ArticleComment).likeArticleComment(commentId)),
		unlikeArticleComment: (commentId: string) =>
			dispatch(new Likes(LikeNodeType.ArticleComment).unlikeArticleComment(commentId)),
		isCommentLiked: (commentId: string, userId: string) =>
			dispatch(new Likes(LikeNodeType.ArticleComment).isCommentLiked(commentId, userId)),
	};
};

ArticleComment = connect(mapStateToPropsArticleComment, mapDispatchToProps)(ArticleComment);

ArticleComment = withAuthContext(ArticleComment);

export default ArticleComment;
